import React from "react";
import { createPortal } from "react-dom";
import { Analytics } from "@vercel/analytics/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isAndroidPlayStoreApp, isPlayStoreApp } from "./lib/appMode";

import Header from "./components/Header";
import Footer from "./components/Footer";
import NativePushManager from "./components/NativePushManager";

// pages
import Home from "./pages/Home";
import Channel from "./pages/Channel";
import Streams from "./pages/Streams";
import Stream from "./pages/Stream";
import ConsoleAwareStream from "./pages/ConsoleAwareStream";
import StreamPlayer from "./pages/StreamPlayer";
import Vods from "./pages/Vods";

import Patches from "./pages/Patches";
import StorePage from "./pages/StorePage";

import Donate from "./pages/Donate";
import DonateSuccess from "./pages/DonateSuccess";
import DonateCancel from "./pages/DonateCancel";

import Wagers from "./pages/Wagers";
import WagersCreate from "./pages/WagersCreate";
import WagerRoom from "./pages/WagerRoom";
import WagerTerms from "./pages/WagerTerms";
import Tournaments from "./pages/Tournaments";
import CrewHeadquartersTournament from "./pages/CrewHeadquartersTournament";
import TournamentLiveHub from "./pages/TournamentLiveHub";
import TournamentDisputeAdmin from "./pages/TournamentDisputeAdmin";
import TournamentPayoutAdmin from "./pages/TournamentPayoutAdmin";
import ManCave from "./pages/ManCave";
import Feed from "./pages/Feed";
import PartnerGames from "./pages/PartnerGames";
import AmericaCoolAuthorize from "./pages/AmericaCoolAuthorize";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Subscribe from "./pages/Subscribe";

import MemberProfile from "./pages/MemberProfile";
import PublicProfile from "./pages/PublicProfile";
import NotFound from "./pages/NotFound";

// OBS tools
import ObsChatDock from "./pages/ObsChatDock";
import OverlayRecord from "./pages/OverlayRecord";
import OverlayCheers from "./pages/OverlayCheers";
import OverlaySpecial from "./pages/OverlaySpecial";
import OverlayRps from "./pages/OverlayRps";
import OverlayGasPoints from "./pages/OverlayGasPoints";
import OverlayTournamentPromo from "./pages/OverlayTournamentPromo";
// Trust/Policy pages
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import QuestionAdmin from "./pages/QuestionAdmin";
import QuestionResponse from "./pages/QuestionResponse";
import NotificationAdmin from "./pages/NotificationAdmin";
import SponsorInbox from "./pages/SponsorInbox";
import AdminPromotion from "./pages/AdminPromotion";
import AdminOperations from "./pages/AdminOperations";
import DigitalVenueEvent from "./pages/DigitalVenueEvent";
import DigitalVenueAdmin from "./pages/DigitalVenueAdmin";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import DeleteAccount from "./pages/DeleteAccount";
import ConsoleStreaming from "./pages/ConsoleStreaming";

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function PageShell({ children }) {
  const location = useLocation();
  const isMobile = useIsMobile(900);

  const isStreamWatchRoute =
    location.pathname === "/stream" || location.pathname.startsWith("/stream/");

  const useMobileWatchShell = isMobile && isStreamWatchRoute;
  const useDesktopWatchSidebar = !isMobile && isStreamWatchRoute;

  if (useMobileWatchShell) {
    return <>{children}</>;
  }

  if (useDesktopWatchSidebar) {
    return (
      <>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <Header variant="streamDesktopSidebar" />
          <div style={{ flex: 1, minWidth: 0, padding: "24px 12px 0 0" }}>{children}</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ paddingTop: 24 }}>{children}</div>
      <Footer />
    </>
  );
}

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function RequireAuth({ children }) {
  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

const VIDEO_BACKGROUND_ROUTES = new Set(["/", "/channels", "/streams", "/man-cave", "/games"]);

function normalizeRoutePath(pathname) {
  const normalized = String(pathname || "/").replace(/\/+$/, "");
  return normalized || "/";
}

function RouteVideoBackground() {
  const location = useLocation();
  const videoRef = React.useRef(null);
  const path = normalizeRoutePath(location.pathname);
  const isVideoRoute = VIDEO_BACKGROUND_ROUTES.has(path);

  React.useEffect(() => {
    if (!isVideoRoute) return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    const startVideo = () => {
      video.muted = true;
      video.defaultMuted = true;

      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {
          // The still wallpaper underneath remains the safe fallback.
        });
      }
    };

    const restartWhenVisible = () => {
      if (!document.hidden) startVideo();
    };

    startVideo();
    video.addEventListener("loadeddata", startVideo);
    video.addEventListener("canplay", startVideo);
    document.addEventListener("visibilitychange", restartWhenVisible);
    window.addEventListener("focus", startVideo);

    return () => {
      video.removeEventListener("loadeddata", startVideo);
      video.removeEventListener("canplay", startVideo);
      document.removeEventListener("visibilitychange", restartWhenVisible);
      window.removeEventListener("focus", startVideo);
    };
  }, [isVideoRoute, path]);

  if (!isVideoRoute || typeof document === "undefined") return null;

  return createPortal(
    <div className="ga-route-video-bg" aria-hidden="true">
      <video
        key={path}
        ref={videoRef}
        className="ga-route-video-bg__video"
        src="/assets/ga_active_streaming_bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        tabIndex={-1}
      />
      <div className="ga-route-video-bg__shade" />
    </div>,
    document.body
  );
}

function BodyRouteClassSync() {
  const location = useLocation();

  React.useEffect(() => {
    const path = normalizeRoutePath(location.pathname);
    const isOverlayRoute =
      path.startsWith("/obs/") ||
      path.startsWith("/overlay/") ||
      path.startsWith("/popout");
    const isVideoRoute = VIDEO_BACKGROUND_ROUTES.has(path);

    document.body.classList.toggle("ga-visual-site", !isOverlayRoute);
    document.body.classList.toggle("ga-video-route", !isOverlayRoute && isVideoRoute);
    document.body.classList.toggle("ga-android-store-app", isAndroidPlayStoreApp());

    return () => {
      document.body.classList.remove("ga-visual-site");
      document.body.classList.remove("ga-video-route");
      document.body.classList.remove("ga-android-store-app");
    };
  }, [location.pathname]);

  return null;
}

export default function App() {
  const playStoreApp = isPlayStoreApp();

  React.useEffect(() => {
    const API_BASE = ((process.env.REACT_APP_API_URL || "https://streamsite-v8dd.onrender.com").trim().replace(/\/$/, "").replace(/\/api$/, ""));
    let playingCount = 0;
    const onPlay = () => { playingCount += 1; };
    const onStop = () => { playingCount = Math.max(0, playingCount - 1); };
    document.addEventListener("play", onPlay, true);
    document.addEventListener("playing", onPlay, true);
    document.addEventListener("pause", onStop, true);
    document.addEventListener("ended", onStop, true);
    const timer = window.setInterval(async () => {
      const token = localStorage.getItem("ga_token") || localStorage.getItem("token") || localStorage.getItem("authToken") || "";
      if (!token || document.visibilityState !== "visible" || playingCount < 1) return;
      try {
        const response = await fetch(`${API_BASE}/api/watch-time/heartbeat`, {
          method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ seconds: 30, playing: true, visible: true, path: window.location.pathname }),
        });
        const data = await response.json().catch(() => null);
        if (response.ok && data?.user) {
          const current = JSON.parse(localStorage.getItem("user") || "{}");
          localStorage.setItem("user", JSON.stringify({ ...current, ...data.user }));
          window.dispatchEvent(new Event("auth:changed"));
        }
      } catch {}
    }, 30000);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("play", onPlay, true);
      document.removeEventListener("playing", onPlay, true);
      document.removeEventListener("pause", onStop, true);
      document.removeEventListener("ended", onStop, true);
    };
  }, []);

  return (
    <>
      <NativePushManager />
      <BodyRouteClassSync />
      <RouteVideoBackground />
      <Routes>
      <Route path="/obs/chat/:channel" element={<ObsChatDock />} />
      <Route path="/obs/record" element={<OverlayRecord />} />
      <Route path="/obs/record/:channelId" element={<OverlayRecord />} />
      <Route path="/overlay/record/:channelId" element={<OverlayRecord />} />
      <Route path="/obs/cheers/:channelId" element={<OverlayCheers />} />
      <Route path="/overlay/cheers/:channelId" element={<OverlayCheers />} />
      <Route path="/obs/special/:channelId" element={<OverlaySpecial />} />
      <Route path="/overlay/special/:channelId" element={<OverlaySpecial />} />
      <Route path="/obs/rps/:channelId" element={<OverlayRps />} />
      <Route path="/overlay/rps/:channelId" element={<OverlayRps />} />
      <Route path="/obs/gas-points/:channelId" element={<OverlayGasPoints />} />
      <Route path="/overlay/gas-points/:channelId" element={<OverlayGasPoints />} />
      <Route path="/obs/tournament-promo/:channelId" element={<OverlayTournamentPromo />} />
      <Route path="/overlay/tournament-promo/:channelId" element={<OverlayTournamentPromo />} />
      <Route path="/integrations/america-cool/authorize" element={<AmericaCoolAuthorize />} />
      <Route path="/america-cool/authorize" element={<AmericaCoolAuthorize />} />
    
      <Route
        path="/"
        element={
          <PageShell>
            <Home />
          </PageShell>
        }
      />

      <Route
        path="/channels"
        element={
          <PageShell>
            <Channel />
          </PageShell>
        }
      />

      <Route
        path="/streams"
        element={
          <PageShell>
            <Streams />
          </PageShell>
        }
      />

      <Route
        path="/stream"
        element={
          <PageShell>
            <Stream />
          </PageShell>
        }
      />

      <Route
        path="/stream/:channel"
        element={
          <PageShell>
            <ConsoleAwareStream />
          </PageShell>
        }
      />

      <Route
        path="/console-streaming"
        element={
          <RequireAuth>
            <PageShell>
              <ConsoleStreaming />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/stream-test/:id"
        element={
          <PageShell>
            <StreamPlayer />
          </PageShell>
        }
      />

      <Route
        path="/vods"
        element={
          <PageShell>
            <Vods />
          </PageShell>
        }
      />

      <Route
        path="/about"
        element={
          <PageShell>
            <About />
          </PageShell>
        }
      />

      <Route
        path="/privacy"
        element={
          <PageShell>
            <Privacy />
          </PageShell>
        }
      />

      <Route
        path="/terms"
        element={
          <PageShell>
            <Terms />
          </PageShell>
        }
      />

      <Route
        path="/contact"
        element={
          <PageShell>
            <Contact />
          </PageShell>
        }
      />

      <Route
        path="/questions/:questionId"
        element={
          <RequireAuth>
            <PageShell>
              <QuestionResponse />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/questions"
        element={
          <RequireAuth>
            <PageShell>
              <QuestionAdmin />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/notifications"
        element={
          <RequireAuth>
            <PageShell>
              <NotificationAdmin />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin"
        element={
          <RequireAuth>
            <PageShell>
              <AdminOperations />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/promotion"
        element={
          <RequireAuth>
            <PageShell>
              <AdminPromotion />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/events/:eventSlug"
        element={
          <PageShell>
            <DigitalVenueEvent />
          </PageShell>
        }
      />

      <Route
        path="/admin/digital-venue"
        element={
          <RequireAuth>
            <PageShell>
              <DigitalVenueAdmin />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/sponsors"
        element={
          <RequireAuth>
            <PageShell>
              <SponsorInbox />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/community-guidelines"
        element={
          <PageShell>
            <CommunityGuidelines />
          </PageShell>
        }
      />

      <Route
        path="/delete-account"
        element={
          <PageShell>
            <DeleteAccount />
          </PageShell>
        }
      />

      <Route
        path="/patches"
        element={
          <PageShell>
            <Patches />
          </PageShell>
        }
      />

      <Route
        path="/store"
        element={
          <PageShell>
            <StorePage />
          </PageShell>
        }
      />

      <Route
        path="/donate"
        element={
          <PageShell>
            <Donate />
          </PageShell>
        }
      />

      <Route
        path="/donate/success"
        element={
          <PageShell>
            <DonateSuccess />
          </PageShell>
        }
      />

      <Route
        path="/donate/cancel"
        element={
          <PageShell>
            <DonateCancel />
          </PageShell>
        }
      />

      <Route
        path="/man-cave"
        element={
          <PageShell>
            <ManCave />
          </PageShell>
        }
      />

      <Route
        path="/wagers"
        element={
          playStoreApp ? (
            <Navigate to="/tournaments" replace />
          ) : (
            <PageShell>
              <Wagers />
            </PageShell>
          )
        }
      />

      <Route
        path="/wagers/create"
        element={
          playStoreApp ? (
            <Navigate to="/tournaments" replace />
          ) : (
            <PageShell>
              <WagersCreate />
            </PageShell>
          )
        }
      />

      <Route
        path="/wagers/terms"
        element={
          playStoreApp ? (
            <Navigate to="/tournaments" replace />
          ) : (
            <PageShell>
              <WagerTerms />
            </PageShell>
          )
        }
      />

      <Route
        path="/wagers/:id"
        element={
          playStoreApp ? (
            <Navigate to="/tournaments" replace />
          ) : (
            <PageShell>
              <WagerRoom />
            </PageShell>
          )
        }
      />

      <Route
        path="/tournaments"
        element={
          <PageShell>
            <Tournaments />
          </PageShell>
        }
      />

      <Route
        path="/tournaments/gas-up-3v3-crew-headquarters"
        element={
          <PageShell>
            <CrewHeadquartersTournament />
          </PageShell>
        }
      />

      <Route
        path="/tournaments/:tournamentSlug/live"
        element={
          <PageShell>
            <TournamentLiveHub />
          </PageShell>
        }
      />

      <Route
        path="/admin/tournament-reviews"
        element={
          <RequireAuth>
            <PageShell>
              <TournamentDisputeAdmin />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/tournament-payouts"
        element={
          <RequireAuth>
            <PageShell>
              <TournamentPayoutAdmin />
            </PageShell>
          </RequireAuth>
        }
      />

      <Route
        path="/games"
        element={
          <PageShell>
            <PartnerGames />
          </PageShell>
        }
      />

      <Route
        path="/games/:slug"
        element={
          <PageShell>
            <PartnerGames />
          </PageShell>
        }
      />

      <Route
        path="/feed"
        element={
          <PageShell>
            <Feed />
          </PageShell>
        }
      />

      <Route
        path="/login"
        element={
          <PageShell>
            <Login />
          </PageShell>
        }
      />

      <Route path="/signin" element={<Navigate to="/login" replace />} />

      <Route
        path="/register"
        element={
          <PageShell>
            <Register />
          </PageShell>
        }
      />

      <Route path="/signup" element={<Navigate to="/register" replace />} />

      <Route
        path="/forgot-password"
        element={
          <PageShell>
            <ForgotPassword />
          </PageShell>
        }
      />

      <Route
        path="/reset-password"
        element={
          <PageShell>
            <ResetPassword />
          </PageShell>
        }
      />

      <Route
        path="/verify-email"
        element={
          <PageShell>
            <VerifyEmail />
          </PageShell>
        }
      />

      <Route
        path="/membership"
        element={
          <PageShell>
            <Subscribe />
          </PageShell>
        }
      />

      <Route path="/subscribe" element={<Navigate to="/membership" replace />} />

      <Route
        path="/u/:username"
        element={
          <PageShell>
            <PublicProfile />
          </PageShell>
        }
      />

      <Route
        path="/profile/:username"
        element={
          <PageShell>
            <PublicProfile />
          </PageShell>
        }
      />

      <Route
        path="/me"
        element={
          <PageShell>
            <RequireAuth>
              <MemberProfile />
            </RequireAuth>
          </PageShell>
        }
      />

      <Route
        path="*"
        element={
          <PageShell>
            <NotFound />
          </PageShell>
        }
      />
      </Routes>
      <Analytics />
    </>
  );
}